<script setup>
/* eslint semi: 1 */

import { inject, computed, ref } from 'vue';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/yup';
import * as yup from 'yup';

import FormInput from '@/components/common/form/form-input-tmpl.vue';
import FormButton from '@/components/common/form/form-button-tmpl.vue';
import SignInWithSocialAccount from '@/components/common/sign-in/sign-in-with-social-account-tmpl.vue';
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';
import { useLocalStorageStore } from '@base-shared/stores/local-storage';
import CONST from '@/globals/constants';

const $utils = inject('$utils');
const $router = inject('$routerInstance');
const cognitoAuthStore = useCognitoAuthStore();

const emit = defineEmits(['emailSubmit', 'redirectToSignUp']);
defineProps({
  isSignInMini: {
    type: Boolean,
    default: false,
  },
  hasSocialSignIn: {
    type: Boolean,
    default: false,
  }
});

const socialSignIn = ref(null);

const productNameShort = computed(() => {
  if ($utils.isPageForLicenses()) {
    return CONST.ACTIVATE;
  }

  return CONFIG.PRODUCT_NAME_SHORT;
});

const schema = toTypedSchema(yup.object({
  email: yup.string()
    .email('label.invalidEmail')
    .transform((value) => value?.toLowerCase?.().trim?.() ?? value)
    .required('label.requiredEmail'),
}));

const { errors, handleSubmit, defineField, isSubmitting } = useForm({
  validationSchema: schema,
});

const [email] = defineField('email');

function redirectToSignUp () {
  if (isSubmitting.value) {
    return;
  }

  emit('redirectToSignUp');
}

const onSubmit = handleSubmit(async (values, actions) => {
  const { error, response: providerInfo } = await cognitoAuthStore.getProviderInfo(values.email);
  if (error) {
    actions.setFieldError('email', 'label.incorrectUsername');
    return;
  }

  if (providerInfo?.SSOProviderName) {
    // If SSI, redirect to /sso page
    $router.push({
      name: 'ssoStartPage',
      params: {
        providerName: providerInfo.SSOProviderName
      }
    });
  } else if ([CONST.GOOGLE, CONST.FACEBOOK].includes(providerInfo?.CognitoIdPName)) {
    if ($router.currentRoute.value?.query?.sso_migrate_account) {
      const ssoMigrateAccount = $utils.decodeBase64Format($router.currentRoute.value?.query?.sso_migrate_account);
      const targetSSOProvider = ssoMigrateAccount?.provider;
      useLocalStorageStore().setIsMigratingFromSocialAccountToStorage(targetSSOProvider, true);
    }
    // If SSI, sign in with the provider
    socialSignIn.value.signInWith(providerInfo.CognitoIdPName);
    return;
  }

  const stage = providerInfo?.CognitoStage ?? CONFIG.GLOBAL_STAGE;

  // To apply transformations to the <input> value
  actions.setFieldValue('email', values.email, false); // false = don't validate on value change

  if (!stage) {
    actions.setFieldError('email', 'label.incorrectUsername');
    return;
  }

  emit('emailSubmit', {
    email: email.value,
    stage,
  });
});
</script>

<template>
  <div v-if="hasSocialSignIn" class="max-[1919px]:text-[10px]/[10px] min-[1920px]:text-xs text-white uppercase">
    <span>{{ $t('signinup.new', { productNameShort }) }}</span>
    <span
      class="underline ml-1"
      :class="{
        'cursor-pointer text-white': !isSubmitting,
        'cursor-not-allowed text-gray-400': isSubmitting
      }"
      @click="redirectToSignUp"
    >
      {{ $t('label.signUp') }}
    </span>
  </div>

  <form
    class="max-[1919px]:my-3 min-[1920px]:my-5 flex flex-col flex-1 w-full justify-between"
    data-component-element-id="sign-in-email-form"
    @submit="onSubmit"
  >
    <!-- Social Sign In -->
    <sign-in-with-social-account
      v-if="hasSocialSignIn"
      ref="socialSignIn"
      class="-mx-3"
      type="login"
      :disabled="isSubmitting"
    />

    <div v-else>
      <!-- Spacer when social sign in is not visible -->
    </div>

    <!-- Form -->
    <div class="flex flex-col">
      <form-input
        id="email"
        autofocus
        name="username"
        v-model="email"
        type="email"
        autocomplete="username"
        :label="$t('label.email')"
        inputTheme="light"
        :size="isSignInMini ? 'xs' : 'md'"
        :disabled="isSubmitting"
        required
        :validationStatus="errors.email ? 'error' : undefined"
        data-component-element-id="sign-in-email-input"
      >
        <template
          v-if="errors?.email"
          #validationTransparent
        >
          {{ $t(errors.email) }}
        </template>
      </form-input>
    </div>

    <form-button
      type="submit"
      button-theme="light"
      data-component-element-id="sign-in-email-submit"
      :size="isSignInMini ? 'xs' : 'md'"
      :disabled="isSubmitting"
      :loading="isSubmitting"
      :label="$t('label.continue')"
      @click="onSubmit"
    />
  </form>
</template>
